import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { webServiceUrl } from "../../../components/apiCall/webserviceurl";
import { toast, ToastContainer } from "react-toastify";

/**
 * RegisterPage — IHFC Cobotics Training Portal
 * Register-only flow (no sign-in tab), full-viewport, fully responsive.
 * Left panel now shows a full-bleed looping video (half the viewport width
 * on desktop). Right panel (the form) is unchanged. On mobile the video
 * panel is hidden entirely — only the form is shown, with a compact
 * brand strip on top (no video there either).
 *
 * No external UI libraries — plain React + inline SVG icons, so it drops
 * into any project without extra dependencies.
 *
 * UPDATE: Step 1 "Program" field now uses a custom-styled dropdown
 * (rp-dropdown) instead of a native <select>, matching the rest of the
 * form's visual language (icon, selected checkmark, animated open/close).
 * Nothing else in the flow was changed.
 */

let PROGRAMS = [
  "Cobotics & Human-Robot Interaction",
  "AI for Robotics",
  "Sensor Fusion & Perception",
  "Industrial Automation Basics",
];

const  video = "/assets/images/plugins/video-1.mp4";

const STEP_LABELS = ["Program", "Details", "Verify", "Password"];

// TODO: replace with your actual hosted video asset (mp4/webm).
// This is the only piece you need to swap in — everything else below
// (layout, sizing, mobile behavior) is already wired up.
const BRAND_VIDEO_SRC = video;
const BRAND_VIDEO_POSTER = "/videos/cobotics-hero-poster.jpg";

// ---- inline icons (no external icon library) ----
const Icon = {
  Bot: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 20}
      height={p.size || 20}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="9" width="16" height="11" rx="2.2" />
      <path d="M12 9V5" />
      <circle cx="12" cy="3.4" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="14" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="14" r="1.3" fill="currentColor" stroke="none" />
      <path d="M2 13h2M20 13h2" />
    </svg>
  ),
  Phone: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 18}
      height={p.size || 18}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.4" />
      <path d="M10.6 18.2h2.8" />
    </svg>
  ),
  User: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 18}
      height={p.size || 18}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20c1.4-3.6 4.4-5.4 7.5-5.4s6.1 1.8 7.5 5.4" />
    </svg>
  ),
  Lock: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 18}
      height={p.size || 18}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7.2a4 4 0 0 1 8 0v3.3" />
    </svg>
  ),
  Eye: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 18}
      height={p.size || 18}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 18}
      height={p.size || 18}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3l18 18" />
      <path d="M10.6 5.2c.45-.07.92-.1 1.4-.1 7 0 10.5 7 10.5 7a15.6 15.6 0 0 1-3.4 4.3M6.7 6.7C3.6 8.6 1.5 12 1.5 12S5 19 12 19c1.3 0 2.5-.2 3.6-.6" />
      <path d="M9.9 10a3 3 0 0 0 4.2 4.2" />
    </svg>
  ),
  Back: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 16}
      height={p.size || 16}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5M11 6l-6 6 6 6" />
    </svg>
  ),
  Check: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 14}
      height={p.size || 14}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
  Refresh: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 14}
      height={p.size || 14}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3.5 12a8.5 8.5 0 0 1 14.6-5.9M20.5 12a8.5 8.5 0 0 1-14.6 5.9" />
      <path d="M18.5 3v3.6H14.9M5.5 21v-3.6h3.6" />
    </svg>
  ),
  Spark: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 40}
      height={p.size || 40}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M8.2 12.6l2.4 2.4 5.2-5.6" />
    </svg>
  ),
  Chevron: (p) => (
    <svg
      viewBox="0 0 24 24"
      width={p.size || 16}
      height={p.size || 16}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
};

const Spinner = () => <span className="rp-spin" aria-hidden="true" />;

// Reusable circuit-trace decoration used on the mobile top strip only now
// (the desktop left panel is video, not the circuit motif).
const CircuitField = ({ variant }) => (
  <svg
    className="rp-circuit"
    viewBox={variant === "wide" ? "0 0 900 220" : "0 0 480 900"}
    preserveAspectRatio="xMidYMid slice"
  >
    {variant === "wide" ? (
      <>
        <g stroke="rgba(226,152,74,0.4)" strokeWidth="1.4" fill="none">
          <path d="M-20 40 H160 V120 H420 V200" />
          <path d="M200 -20 V60 H520 V160 H900" />
          <path d="M700 -20 V80 H920" />
        </g>
        <g fill="rgba(226,152,74,0.6)">
          <circle cx="160" cy="40" r="3.4" />
          <circle cx="420" cy="120" r="3.4" />
          <circle cx="520" cy="160" r="3.4" />
          <circle cx="200" cy="60" r="3.4" />
          <circle cx="700" cy="80" r="3.4" />
        </g>
      </>
    ) : (
      <>
        <g stroke="rgba(226,152,74,0.35)" strokeWidth="1.4" fill="none">
          <path d="M-20 120 H140 V220 H360 V340" />
          <path d="M-20 420 H90 V520 H260 V600 H480" />
          <path d="M-20 700 H180 V780 H500" />
          <path d="M480 60 H320 V180" />
        </g>
        <g fill="rgba(226,152,74,0.55)">
          <circle cx="140" cy="120" r="4" />
          <circle cx="360" cy="220" r="4" />
          <circle cx="90" cy="420" r="4" />
          <circle cx="260" cy="520" r="4" />
          <circle cx="180" cy="700" r="4" />
          <circle cx="320" cy="180" r="4" />
        </g>
        <g stroke="rgba(255,255,255,0.06)" strokeWidth="1">
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={i} x1="-20" x2="500" y1={i * 110} y2={i * 110} />
          ))}
        </g>
      </>
    )}
  </svg>
);

const RegisterPage = () => {
  const [step, setStep] = useState(1); // 1 program, 2 details, 3 otp, 4 password, 5 done
  const [direction, setDirection] = useState("fwd");

  const [program, setProgram] = useState("");
  const [programOpen, setProgramOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  console.log(token);
  const [programData, setprogramData] = useState([]);
  const [loading, setloading] = useState(false);
  const [schoolCode, setschoolCode] = useState("");

  const getCohortData = async () => {
    try {
      setloading(true);
      let res = await axios.get(webServiceUrl.all_programs, {
        // headers: {
        //   Authorization: `Bearer ${token}`,
        // },
      });
      console.log(res?.data?.data);
      if (res?.data?.success) {
        setProgramOpen(true);
        setloading(false);
        setprogramData(res?.data?.data);
        // PROGRAMS = res?.data;
      }
    } catch (e) {
      setloading(false);
      console.log(e, "error");
    }
  };

  const otpRefs = useRef([]);
  const programRef = useRef(null);

  // Close the program dropdown on outside click or Escape.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (programRef.current && !programRef.current.contains(e.target)) {
        setProgramOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setProgramOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const goToStep = (n, dir) => {
    setDirection(dir);
    setStep(n);
  };

  const startResendTimer = () => {
    setResendSeconds(30);
    const interval = setInterval(() => {
      setResendSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const selectProgram = (p) => {
    setProgram(p);
    setProgramOpen(false);
    setErrors((prev) => ({ ...prev, program: undefined }));
  };

  const goToDetails = () => {
    if (!program) {
      setErrors({ program: "Choose a program to continue" });
      return;
    }
    setErrors({});
    goToStep(2, "fwd");
  };

  const resendOtp = async () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Enter your full name";
    if (!/^\d{10}$/.test(phone))
      newErrors.phone = "Enter a valid 10-digit number";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    try {
      // TODO: await axios.post(webServiceUrl.send_otp, { phone, program, name });
      let res = await axios.post(webServiceUrl.resend_otp, {
        school_code: schoolCode,
        phone_res: phone,
      });

      if (res?.data?.success) {
        goToStep(3, "fwd");
        startResendTimer();
      }

      console.log(res);

      // if (res)

      // goToStep(3, "fwd");
    } catch (e) {
      console.log(e);
      toast.error(e?.message);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  const sendOtp = async () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Enter your full name";
    if (!/^\d{10}$/.test(phone))
      newErrors.phone = "Enter a valid 10-digit number";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    try {
      // TODO: await axios.post(webServiceUrl.send_otp, { phone, program, name });
      let res = await axios.post(webServiceUrl.register_user, {
        school_code: schoolCode,
        name: name,
        phone_res: phone,
      });

      if (res?.data?.success) {
        goToStep(3, "fwd");
        startResendTimer();
      }

      console.log(res);

      // if (res)

      // goToStep(3, "fwd");
    } catch (e) {
      console.log(e);
      toast.error(e?.message);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.split("");
    while (next.length < 6) next.push("");
    setOtp(next);
    const lastIdx = Math.min(pasted.length, 6) - 1;
    otpRefs.current[lastIdx]?.focus();
  };

  const verifyOtp = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      setErrors({ otp: "Enter the full 6-digit code" });
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    try {
      // TODO: await axios.post(webServiceUrl.send_otp, { phone, program, name });
      let res = await axios.post(webServiceUrl.verify_otp, {
        school_code: schoolCode,
        phone_res: phone,
        otp: code,
      });

      console.log(res);
      if (res?.data?.success) {
        goToStep(4, "fwd");
        localStorage.setItem("token", res?.data?.token);
        // startResendTimer();
      }

      console.log(res);

      // if (res)

      // goToStep(3, "fwd");
    } catch (e) {
      console.log(e?.message, "pressings");
      toast.error(e?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordChecks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    number: /\d/.test(password),
  };
  const passwordStrength = Object.values(passwordChecks).filter(Boolean).length;
  const strengthLabel = ["Too short", "Weak", "Good", "Strong"][
    passwordStrength
  ];

  const generatePassword = () => {
    const chars =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$";
    let pass = "";
    for (let i = 0; i < 10; i++)
      pass += chars[Math.floor(Math.random() * chars.length)];
    setPassword(pass);
    setConfirmPassword(pass);
    setShowPassword(true);
  };

  const completeRegistration = async () => {
    const newErrors = {};
    if (passwordStrength < 3) newErrors.password = "Password is too weak";
    if (password !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    try {
      // TODO: await axios.post(webServiceUrl.send_otp, { phone, program, name });
      let res = await axios.post(
        webServiceUrl.generate_password,
        {
          password: password,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      console.log(res);
      if (res?.data?.success) {
        goToStep(5, "fwd");

        // startResendTimer();
      }

      console.log(res);

      // if (res)

      // goToStep(3, "fwd");
    } catch (e) {
      console.log(e?.message, "pressings");
      toast.error(e?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rp-root">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        style={{ fontSize: "15px" }}
      />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');

        :root{
          --pine-900:#092818;
          --pine-800:#0f3d26;
          --pine-700:#145c34;
          --pine-600:#1c7a45;
          --copper:#e2984a;
          --copper-dim:#f0c898;
          --paper:#f6f7f4;
          --paper-2:#ffffff;
          --ink:#12241b;
          --ink-soft:#5c6b62;
          --line:#e2e7e1;
          --danger:#c8493a;
          --shadow-sm:0 1px 2px rgba(9,40,24,0.06);
          --shadow-md:0 8px 24px rgba(9,40,24,0.08);
          --shadow-lg:0 20px 48px rgba(9,40,24,0.12);
        }
        *{box-sizing:border-box;}
        .rp-root{
          font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
          color:var(--ink);
          width:100%;
          min-height:100vh;
          min-height:100dvh;
          display:flex;
          background:var(--paper);
        }

        /* ---------- Left panel: full-bleed video, half the viewport ---------- */
        .rp-brand{
          position:relative;
          flex:0 0 50%;
          max-width:50%;
          min-width:0;
          background:var(--pine-900); /* shows while video loads */
          overflow:hidden;
        }
        .rp-brand-video{
          position:absolute;
          inset:0;
          width:100%;
          height:100%;
          object-fit:cover;
          display:block;
        }
        /* subtle scrim so the panel reads as intentional, not a raw clip */
        .rp-brand::after{
          content:'';
          position:absolute;
          inset:0;
          background:linear-gradient(180deg, rgba(9,40,24,0.15) 0%, rgba(9,40,24,0) 30%, rgba(9,40,24,0.35) 100%);
          pointer-events:none;
        }

        /* Mobile brand strip — compact identity bar, no video */
        .rp-brand-mobile{
          display:none;
          position:relative;
          background:
            radial-gradient(140% 220% at 10% 0%, var(--pine-700) 0%, var(--pine-800) 50%, var(--pine-900) 100%);
          color:#eef4ef;
          padding:18px 20px;
          overflow:hidden;
          align-items:center;
          gap:12px;
        }
        .rp-brand-mobile .rp-mark{
          width:38px;height:38px;border-radius:11px;
          background:rgba(226,152,74,0.14);
          border:1px solid rgba(226,152,74,0.35);
          color:var(--copper);
          display:flex;align-items:center;justify-content:center;
          flex:none;
        }
        .rp-brand-mobile-text{position:relative;z-index:1;min-width:0;}
        .rp-brand-mobile-eyebrow{
          font-family:'JetBrains Mono',monospace;
          font-size:0.62rem;
          letter-spacing:0.1em;
          text-transform:uppercase;
          color:var(--copper-dim);
        }
        .rp-brand-mobile-title{
          font-family:'Space Grotesk',sans-serif;
          font-weight:600;
          font-size:0.94rem;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .rp-circuit{
          position:absolute;
          inset:0;
          width:100%;
          height:100%;
          opacity:0.55;
          pointer-events:none;
        }

        /* ---------- Right panel: the form — unchanged ---------- */
        .rp-form-side{
          flex:1;
          min-width:0;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          padding:clamp(20px,4vw,40px);
          overflow-y:auto;
        }
        .rp-panel{
          width:100%;
          max-width:440px;
        }
        .rp-panel-head{
          display:flex;
          align-items:baseline;
          justify-content:space-between;
          margin-bottom:clamp(18px,3vh,28px);
        }
        .rp-kicker{
          font-family:'JetBrains Mono',monospace;
          font-size:0.7rem;
          letter-spacing:0.1em;
          text-transform:uppercase;
          color:var(--ink-soft);
        }
        .rp-step-index{
          font-family:'JetBrains Mono',monospace;
          font-size:0.78rem;
          color:var(--pine-700);
          font-weight:600;
          background:rgba(20,92,52,0.08);
          padding:3px 9px;
          border-radius:999px;
        }

        /* PCB trace rail */
        .rp-rail{
          display:flex;
          align-items:center;
          margin-bottom:clamp(22px,4vh,32px);
        }
        .rp-node-wrap{display:flex;flex-direction:column;align-items:center;flex:none;}
        .rp-node{
          width:26px;height:26px;border-radius:7px;
          background:var(--paper-2);
          border:1.6px solid var(--line);
          color:var(--ink-soft);
          display:flex;align-items:center;justify-content:center;
          font-family:'JetBrains Mono',monospace;
          font-size:0.68rem;font-weight:600;
          transition:all .25s ease;
          flex:none;
        }
        .rp-node--active{
          background:var(--pine-700);
          border-color:var(--pine-700);
          color:#fff;
          box-shadow:0 0 0 4px rgba(20,92,52,0.14);
        }
        .rp-node--done{
          background:var(--copper);
          border-color:var(--copper);
          color:#fff;
        }
        .rp-node-label{
          margin-top:6px;
          font-size:0.62rem;
          letter-spacing:0.03em;
          color:var(--ink-soft);
          white-space:nowrap;
        }
        .rp-trace{
          flex:1;
          height:2px;
          background:var(--line);
          margin:0 4px 18px;
          position:relative;
          overflow:hidden;
        }
        .rp-trace::after{
          content:'';
          position:absolute;inset:0;
          background:var(--copper);
          transform:scaleX(0);
          transform-origin:left;
          transition:transform .35s ease;
        }
        .rp-trace--done::after{transform:scaleX(1);}

        .rp-mobile-progress{
          display:none;
          align-items:center;
          gap:10px;
          margin-bottom:20px;
        }
        .rp-mobile-progress-track{
          flex:1;height:4px;border-radius:999px;
          background:var(--line);overflow:hidden;
        }
        .rp-mobile-progress-fill{
          height:100%;background:var(--copper);border-radius:999px;
          transition:width .3s ease;
        }
        .rp-mobile-progress-text{
          font-family:'JetBrains Mono',monospace;
          font-size:0.72rem;color:var(--ink-soft);white-space:nowrap;
        }

        .rp-step-body{
          animation:rp-fade-in .32s cubic-bezier(.2,.7,.3,1);
        }
        @keyframes rp-fade-in{
          from{opacity:0;transform:translateY(8px);}
          to{opacity:1;transform:translateY(0);}
        }

        .rp-title{
          font-family:'Space Grotesk',sans-serif;
          font-weight:700;
          font-size:clamp(1.3rem,3vw,1.55rem);
          margin-bottom:6px;
          letter-spacing:-0.01em;
        }
        .rp-subtitle{
          font-size:0.88rem;
          color:var(--ink-soft);
          margin-bottom:24px;
          line-height:1.55;
        }

        .rp-back{
          display:inline-flex;align-items:center;gap:5px;
          font-size:0.82rem;color:var(--ink-soft);
          background:none;border:none;cursor:pointer;
          margin-bottom:16px;padding:4px 0;
          transition:color .15s ease, transform .15s ease;
        }
        .rp-back:hover{color:var(--pine-700);transform:translateX(-2px);}

        .rp-field{margin-bottom:18px;}
        .rp-label{
          display:block;
          font-size:0.78rem;font-weight:600;
          color:var(--ink);
          margin-bottom:7px;
        }
        .rp-input-wrap{
          display:flex;align-items:center;gap:9px;
          border:1.5px solid var(--line);
          border-radius:11px;
          padding:0 13px;
          background:var(--paper-2);
          transition:border-color .15s ease, box-shadow .15s ease;
        }
        .rp-input-wrap.has-error{
          border-color:var(--danger);
        }
        .rp-input-wrap svg{flex:none;color:var(--ink-soft);}
        .rp-input{
          border:none;outline:none;background:transparent;
          padding:12px 0;width:100%;
          font-size:0.96rem;color:var(--ink);
          font-family:inherit;
          min-width:0;
        }
        .rp-input:focus,
        .rp-input:focus-visible{
          outline:none;
          box-shadow:none;
        }

        /* ---------- Custom program dropdown ---------- */
        .rp-dropdown{position:relative;}
        .rp-dropdown-trigger{
          width:100%;
          display:flex;align-items:center;gap:10px;
          border:1.5px solid var(--line);
          border-radius:11px;
          padding:12px 13px;
          background:var(--paper-2);
          cursor:pointer;
          font-family:inherit;
          font-size:0.96rem;
          color:var(--ink);
          text-align:left;
          transition:border-color .15s ease, box-shadow .15s ease;
        }
        .rp-dropdown-trigger:hover{border-color:var(--pine-600);}
        .rp-dropdown-trigger.open{
          border-color:var(--pine-700);
          box-shadow:0 0 0 4px rgba(20,92,52,0.12);
        }
        .rp-dropdown-trigger.has-error{border-color:var(--danger);}
        .rp-dropdown-trigger-icon{
          flex:none;display:flex;
          color:var(--pine-600);
        }
        .rp-dropdown-value{
          flex:1;min-width:0;
          overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
        }
        .rp-dropdown-value.placeholder{color:var(--ink-soft);}
        .rp-dropdown-caret{
          flex:none;display:flex;color:var(--ink-soft);
          transition:transform .2s ease;
        }
        .rp-dropdown-trigger.open .rp-dropdown-caret{transform:rotate(180deg);color:var(--pine-700);}

        .rp-dropdown-menu{
          position:absolute;
          top:calc(100% + 8px);
          left:0;right:0;
          background:var(--paper-2);
          border:1.5px solid var(--line);
          border-radius:12px;
          box-shadow:var(--shadow-lg);
          padding:6px;
          z-index:20;
          list-style:none;
          margin:0;
          max-height:260px;
          overflow-y:auto;
          animation:rp-dropdown-in .18s cubic-bezier(.2,.7,.3,1);
        }
        @keyframes rp-dropdown-in{
          from{opacity:0;transform:translateY(-6px) scale(0.98);}
          to{opacity:1;transform:translateY(0) scale(1);}
        }
        .rp-dropdown-option{
          display:flex;align-items:center;justify-content:space-between;gap:10px;
          padding:10px 11px;
          border-radius:8px;
          font-size:0.87rem;
          color:var(--ink);
          cursor:pointer;
          transition:background .12s ease, color .12s ease;
        }
        .rp-dropdown-option + .rp-dropdown-option{margin-top:2px;}
        .rp-dropdown-option:hover{background:rgba(20,92,52,0.07);}
        .rp-dropdown-option.selected{
          background:rgba(20,92,52,0.1);
          color:var(--pine-700);
          font-weight:600;
        }
        .rp-dropdown-option.selected svg{color:var(--pine-700);flex:none;}
        .rp-dropdown-option-text{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}

        .rp-eye-btn{
          border:none;background:none;color:var(--ink-soft);
          display:flex;cursor:pointer;padding:6px;margin:-6px;
          border-radius:6px;
          transition:color .15s ease;
        }
        .rp-eye-btn:hover{color:var(--pine-700);}
        .rp-error{
          display:flex;align-items:center;gap:5px;
          color:var(--danger);
          font-size:0.76rem;
          margin-top:6px;
        }

        .rp-btn{
          width:100%;
          border:none;border-radius:11px;
          padding:14px 0;
          font-size:0.94rem;font-weight:600;
          font-family:'Inter',sans-serif;
          cursor:pointer;
          display:flex;align-items:center;justify-content:center;gap:8px;
          transition:background .18s ease, transform .1s ease, box-shadow .18s ease;
          min-height:48px;
        }
        .rp-btn--primary{
          background:var(--pine-700);
          color:#fff;
          box-shadow:0 1px 2px rgba(9,40,24,0.15), 0 8px 20px rgba(20,92,52,0.18);
        }
        .rp-btn--primary:hover:not(:disabled){
          background:var(--pine-800);
          box-shadow:0 1px 2px rgba(9,40,24,0.18), 0 10px 26px rgba(20,92,52,0.26);
        }
        .rp-btn--primary:disabled{opacity:0.65;cursor:not-allowed;box-shadow:none;}
        .rp-btn:active:not(:disabled){transform:translateY(1px);}
        .rp-btn:focus-visible,
        .rp-back:focus-visible,
        .rp-gen-link:focus-visible,
        .rp-eye-btn:focus-visible,
        .rp-dropdown-trigger:focus-visible,
        .rp-resend button:focus-visible{
          outline:2px solid var(--pine-700);
          outline-offset:2px;
        }

        .rp-otp-row{display:flex;gap:9px;margin-bottom:8px;}
        .rp-otp-box{
          width:100%;aspect-ratio:1/1;
          text-align:center;font-size:1.2rem;font-weight:700;
          border-radius:10px;border:1.5px solid var(--line);
          background:var(--paper-2);
          font-family:'JetBrains Mono',monospace;
          outline:none;
          transition:border-color .15s ease, box-shadow .15s ease, transform .1s ease;
        }
        .rp-otp-box:focus{
          outline:none;
          border-color:var(--line);
          box-shadow:none;
        }
        .rp-resend{font-size:0.82rem;color:var(--ink-soft);margin-bottom:22px;}
        .rp-resend button{
          border:none;background:none;color:var(--pine-700);
          font-weight:600;padding:0;cursor:pointer;
        }
        .rp-resend button:disabled{color:#a8b6b0;cursor:default;}

        .rp-gen-link{
          display:inline-flex;align-items:center;gap:6px;
          font-size:0.8rem;font-weight:600;color:var(--copper);
          background:rgba(226,152,74,0.1);
          border:1px solid rgba(226,152,74,0.3);
          border-radius:8px;
          padding:8px 12px;
          cursor:pointer;margin-bottom:18px;
          transition:background .15s ease, border-color .15s ease;
        }
        .rp-gen-link:hover{background:rgba(226,152,74,0.18);border-color:rgba(226,152,74,0.5);}

        .rp-strength-row{display:flex;align-items:center;gap:10px;margin:8px 0 12px;}
        .rp-strength-bars{display:flex;gap:6px;flex:1;}
        .rp-strength-bar{height:5px;flex:1;border-radius:999px;background:var(--line);transition:background .2s ease;}
        .rp-strength-bar--w{background:var(--danger);}
        .rp-strength-bar--m{background:var(--copper);}
        .rp-strength-bar--s{background:var(--pine-700);}
        .rp-strength-label{
          font-size:0.72rem;font-weight:600;color:var(--ink-soft);
          white-space:nowrap;font-family:'JetBrains Mono',monospace;
        }
        .rp-check-list{font-size:0.78rem;color:var(--ink-soft);margin-bottom:20px;display:flex;flex-direction:column;gap:6px;}
        .rp-check-list div{display:flex;align-items:center;gap:7px;transition:color .15s ease;}
        .rp-check-list .met{color:var(--pine-700);}
        .rp-check-list svg{opacity:0.35;flex:none;}
        .rp-check-list .met svg{opacity:1;}

        .rp-success{text-align:center;padding:20px 0 6px;}
        .rp-success-icon{
          width:66px;height:66px;border-radius:50%;
          background:rgba(20,92,52,0.1);
          color:var(--pine-700);
          display:flex;align-items:center;justify-content:center;
          margin:0 auto 20px;
          animation:rp-pop .4s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes rp-pop{
          from{transform:scale(0.6);opacity:0;}
          to{transform:scale(1);opacity:1;}
        }

        .rp-spin{
          width:15px;height:15px;border-radius:50%;
          border:2px solid rgba(255,255,255,0.4);
          border-top-color:#fff;
          animation:rp-spin 0.7s linear infinite;
          flex:none;
        }
        @keyframes rp-spin{to{transform:rotate(360deg);}}

        /* ---------- Responsive breakpoints ---------- */
        @media (max-width:960px){
          .rp-brand{flex-basis:45%;max-width:45%;}
        }

        @media (max-width:860px){
          .rp-root{flex-direction:column;min-height:100vh;min-height:100dvh;}
          /* Video panel fully removed on mobile — form only, per spec */
          .rp-brand{display:none;}
          .rp-brand-mobile{display:flex;}
          .rp-form-side{padding:22px 18px 32px;}
          .rp-panel{max-width:520px;}
          .rp-rail{display:none;}
          .rp-mobile-progress{display:flex;}
        }

        @media (max-width:480px){
          .rp-form-side{padding:18px 14px 28px;}
          .rp-otp-row{gap:7px;}
          .rp-otp-box{font-size:1.05rem;border-radius:9px;}
          .rp-title{font-size:1.25rem;}
          .rp-brand-mobile{padding:14px 16px;}
          .rp-btn{padding:13px 0;}
        }

        @media (max-width:360px){
          .rp-otp-row{gap:5px;}
        }

        @media (prefers-reduced-motion: reduce){
          .rp-spin{animation:none;}
          .rp-step-body{animation:none;}
          .rp-success-icon{animation:none;}
          .rp-back:hover{transform:none;}
          .rp-dropdown-menu{animation:none;}
        }
      `}</style>

      {/* Desktop left panel — full-bleed looping video, half the viewport.
          Hidden entirely on mobile via the .rp-brand{display:none} rule above. */}
      <div className="rp-brand">
        {/* <video
          className="rp-brand-video"
          src={"https://youtu.be/LxBS2aSQ2UI?si=Nri_BybfbIhhfLy5"}
          poster={BRAND_VIDEO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        /> */}
        <iframe
          className="rp-brand-video"
          src="https://www.youtube.com/embed/LxBS2aSQ2UI?autoplay=1&mute=1&loop=1&playlist=LxBS2aSQ2UI&controls=0&modestbranding=1&rel=0&iv_load_policy=3&disablekb=1"
          title="Brand Video"
          frameBorder="0"
          allow="autoplay; encrypted-media;picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>

      {/* Mobile brand strip — compact identity bar, no video on mobile */}
      <div className="rp-brand-mobile">
        <CircuitField variant="wide" />
        <div className="rp-mark">
          <Icon.Bot size={20} />
        </div>
        <div className="rp-brand-mobile-text">
          <div className="rp-brand-mobile-eyebrow">IHFC · IIT Delhi</div>
          <div className="rp-brand-mobile-title">Cobotics Training Program</div>
        </div>
      </div>

      {/* Form panel — unchanged */}
      <div className="rp-form-side">
        <div className="rp-panel">
          <div className="rp-panel-head">
            <span className="rp-kicker">Register</span>
            {step <= 4 && <span className="rp-step-index">0{step} / 04</span>}
          </div>

          {step <= 4 && (
            <>
              <div className="rp-rail">
                {STEP_LABELS.map((label, idx) => {
                  const num = idx + 1;
                  const state =
                    num < step ? "done" : num === step ? "active" : "";
                  return (
                    <React.Fragment key={label}>
                      <div className="rp-node-wrap">
                        <div
                          className={`rp-node ${state === "active" ? "rp-node--active" : ""} ${state === "done" ? "rp-node--done" : ""}`}
                        >
                          {state === "done" ? <Icon.Check /> : num}
                        </div>
                        <span className="rp-node-label">{label}</span>
                      </div>
                      {idx < STEP_LABELS.length - 1 && (
                        <div
                          className={`rp-trace ${num < step ? "rp-trace--done" : ""}`}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              <div className="rp-mobile-progress">
                <span className="rp-mobile-progress-text">
                  {STEP_LABELS[step - 1]}
                </span>
                <div className="rp-mobile-progress-track">
                  <div
                    className="rp-mobile-progress-fill"
                    style={{ width: `${(step / 4) * 100}%` }}
                  />
                </div>
                <span className="rp-mobile-progress-text">{step}/4</span>
              </div>
            </>
          )}

          <div className="rp-step-body" key={step}>
            {step === 1 && (
              <>
                <div className="rp-title">Choose your track</div>
                <div className="rp-subtitle">
                  Select the training program you'd like to register for.
                </div>
                <div className="rp-field">
                  <label className="rp-label" htmlFor="rp-program">
                    Program
                  </label>
                  <div className="rp-dropdown" ref={programRef}>
                    <button
                      type="button"
                      id="rp-program"
                      className={`rp-dropdown-trigger ${errors.program ? "has-error" : ""} ${programOpen ? "open" : ""}`}
                      onClick={() => getCohortData()}
                      aria-haspopup="listbox"
                      aria-expanded={programOpen}
                    >
                      <span className="rp-dropdown-trigger-icon">
                        <Icon.Bot size={17} />
                      </span>
                      <span
                        className={`rp-dropdown-value ${!program ? "placeholder" : ""}`}
                      >
                        {loading
                          ? "Loading ...."
                          : program || "Select a program"}
                      </span>
                      <span className="rp-dropdown-caret">
                        <Icon.Chevron size={16} />
                      </span>
                    </button>

                    {programOpen && (
                      <ul className="rp-dropdown-menu" role="listbox">
                        {programData.map((p) => (
                          <li
                            key={p}
                            role="option"
                            aria-selected={program === p?.name}
                            className={`rp-dropdown-option ${program?.name === p?.name ? "selected" : ""}`}
                            onClick={() => {
                              selectProgram(p?.name);

                              setschoolCode(p?.school_code);
                            }}
                          >
                            <span className="rp-dropdown-option-text">
                              {p?.name}
                            </span>
                            {program === p?.name && <Icon.Check size={14} />}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {errors.program && (
                    <div className="rp-error">{errors.program}</div>
                  )}
                </div>
                <button
                  className="rp-btn rp-btn--primary"
                  onClick={goToDetails}
                >
                  Continue
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <button className="rp-back" onClick={() => goToStep(1, "back")}>
                  <Icon.Back /> Back
                </button>
                <div className="rp-title">Your details</div>
                <div className="rp-subtitle">
                  We'll send a one-time code to verify your phone number.
                </div>
                <div className="rp-field">
                  <label className="rp-label" htmlFor="rp-name">
                    Full name
                  </label>
                  <div
                    className={`rp-input-wrap ${errors.name ? "has-error" : ""}`}
                  >
                    <Icon.User />
                    <input
                      id="rp-name"
                      className="rp-input"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  {errors.name && <div className="rp-error">{errors.name}</div>}
                </div>
                <div className="rp-field">
                  <label className="rp-label" htmlFor="rp-phone">
                    Phone number
                  </label>
                  <div
                    className={`rp-input-wrap ${errors.phone ? "has-error" : ""}`}
                  >
                    <Icon.Phone />
                    <input
                      id="rp-phone"
                      className="rp-input"
                      placeholder="10-digit mobile number"
                      value={phone}
                      inputMode="numeric"
                      maxLength={10}
                      onChange={(e) =>
                        setPhone(e.target.value.replace(/\D/g, ""))
                      }
                    />
                  </div>
                  {errors.phone && (
                    <div className="rp-error">{errors.phone}</div>
                  )}
                </div>
                <button
                  className="rp-btn rp-btn--primary"
                  onClick={sendOtp}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Spinner />} Send OTP
                </button>
              </>
            )}

            {step === 3 && (
              <>
                <button className="rp-back" onClick={() => goToStep(2, "back")}>
                  <Icon.Back /> Back
                </button>
                <div className="rp-title">Verify your number</div>
                <div className="rp-subtitle">
                  Enter the 6-digit code sent to {phone || "your phone"}.
                </div>
                <div className="rp-otp-row" onPaste={handleOtpPaste}>
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (otpRefs.current[idx] = el)}
                      className="rp-otp-box"
                      value={digit}
                      maxLength={1}
                      inputMode="numeric"
                      aria-label={`Digit ${idx + 1}`}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    />
                  ))}
                </div>
                {errors.otp && (
                  <div className="rp-error" style={{ marginBottom: 10 }}>
                    {errors.otp}
                  </div>
                )}
                <div className="rp-resend">
                  Didn't get a code?{" "}
                  <button disabled={resendSeconds > 0} onClick={resendOtp}>
                    {resendSeconds > 0
                      ? `Resend in ${resendSeconds}s`
                      : "Resend OTP"}
                  </button>
                </div>
                <button
                  className="rp-btn rp-btn--primary"
                  onClick={verifyOtp}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Spinner />} Verify
                </button>
              </>
            )}

            {step === 4 && (
              <>
                <button className="rp-back" onClick={() => goToStep(3, "back")}>
                  <Icon.Back /> Back
                </button>
                <div className="rp-title">Create a password</div>
                <div className="rp-subtitle">
                  Set a password for your account, or generate a secure one.
                </div>

                <button className="rp-gen-link" onClick={generatePassword}>
                  <Icon.Refresh /> Generate a strong password
                </button>

                <div className="rp-field" style={{ marginBottom: 4 }}>
                  <label className="rp-label" htmlFor="rp-password">
                    Password
                  </label>
                  <div
                    className={`rp-input-wrap ${errors.password ? "has-error" : ""}`}
                  >
                    <Icon.Lock />
                    <input
                      id="rp-password"
                      className="rp-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="rp-eye-btn"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <Icon.EyeOff /> : <Icon.Eye />}
                    </button>
                  </div>
                </div>

                <div className="rp-strength-row">
                  <div className="rp-strength-bars">
                    <div
                      className={`rp-strength-bar ${passwordStrength >= 1 ? "rp-strength-bar--w" : ""}`}
                    />
                    <div
                      className={`rp-strength-bar ${passwordStrength >= 2 ? "rp-strength-bar--m" : ""}`}
                    />
                    <div
                      className={`rp-strength-bar ${passwordStrength >= 3 ? "rp-strength-bar--s" : ""}`}
                    />
                  </div>
                  {password.length > 0 && (
                    <span className="rp-strength-label">{strengthLabel}</span>
                  )}
                </div>
                <div className="rp-check-list">
                  <div className={passwordChecks.length ? "met" : ""}>
                    <Icon.Check /> At least 8 characters
                  </div>
                  <div className={passwordChecks.upper ? "met" : ""}>
                    <Icon.Check /> One uppercase letter
                  </div>
                  <div className={passwordChecks.number ? "met" : ""}>
                    <Icon.Check /> One number
                  </div>
                </div>

                <div className="rp-field">
                  <label className="rp-label" htmlFor="rp-confirm">
                    Confirm password
                  </label>
                  <div
                    className={`rp-input-wrap ${errors.confirmPassword ? "has-error" : ""}`}
                  >
                    <Icon.Lock />
                    <input
                      id="rp-confirm"
                      className="rp-input"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                  {errors.password && (
                    <div className="rp-error">{errors.password}</div>
                  )}
                  {errors.confirmPassword && (
                    <div className="rp-error">{errors.confirmPassword}</div>
                  )}
                </div>

                <button
                  className="rp-btn rp-btn--primary"
                  onClick={completeRegistration}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Spinner />} Create account
                </button>
              </>
            )}

            {step === 5 && (
              <div className="rp-success">
                <div className="rp-success-icon">
                  <Icon.Spark size={32} />
                </div>
                <div className="rp-title">You're all set!</div>
                <div className="rp-subtitle">
                  Your account has been created. Head to the sign-in page to
                  access your dashboard.
                </div>
                <button
                  className="rp-btn rp-btn--primary"
                  onClick={() => {
                    const encodedCode = btoa(schoolCode.toString());
                    localStorage.setItem("encoded", encodedCode);
                    navigate(`/auth/sign-in?school_code=${encodedCode}`);
                  }}
                >
                  Continue to sign in
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
